(() => {
  'use strict';
  if (window.UFMWishlistInitialized) return;
  window.UFMWishlistInitialized = true;
  let config = {};
  try { config = JSON.parse(document.getElementById('ufm-compare-config')?.textContent || '{}') || {}; }
  catch (_) { /* Missing configuration must never imply a successful save. */ }
  const account = window.UFMSavedLists;
  const enabled = Boolean(account?.enabled);
  const loggedIn = typeof config.customer === 'string' && Boolean(config.customer.trim());
  const unavailable = 'Not saved. Wishlist account integration is unavailable. Please try again when the service is available.';
  const page = document.querySelector('[data-ufm-wishlist-page]');
  const feedback = document.createElement('p');
  feedback.className = 'ufm-wishlist-feedback';
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite');
  feedback.hidden = true;
  document.body.append(feedback);
  const busy = new Set();
  let renderVersion = 0;

  function message(text) {
    const status = page?.querySelector('[data-ufm-wishlist-message]') || document.querySelector('.ufm-product-page__saved-action-status') || feedback;
    status.textContent = text;
    status.hidden = false;
  }

  function identity(value) {
    if (typeof value !== 'string' && !(typeof value === 'number' && Number.isSafeInteger(value))) return null;
    return /^\d+$/.test(String(value)) ? String(value).replace(/^0+/, '') || null : null;
  }

  function productUrl(path) {
    if (typeof path !== 'string' || !path || /[\s\\]/.test(path)) throw new Error('Invalid wishlist product URL.');
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || url.username || url.password ||
        !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) {
      throw new Error('Invalid wishlist product URL.');
    }
    return url;
  }

  function normalize(value) {
    const id = identity(value?.id);
    const variant = identity(value?.variant);
    if (!id || !variant) throw new Error('Invalid wishlist product or variant ID.');
    const url = productUrl(value.url);
    return { id, variant, url: url.pathname + url.search + url.hash };
  }

  function read() {
    const entries = account.read('wishlist');
    if (!Array.isArray(entries)) throw new Error('Wishlist account data is unavailable.');
    return entries.map(normalize);
  }

  async function data(entry) {
    const url = productUrl(entry.url);
    url.search = '?view=ufm-compare-data';
    url.hash = '';
    const response = await fetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Product information is unavailable.');
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const payload = parsed.querySelector('[data-ufm-compare-data]');
    if (!payload) throw new Error('Wishlist product data is unavailable.');
    const product = JSON.parse(payload.textContent);
    if (identity(product?.id) !== entry.id) throw new Error('Wishlist product identity does not match.');
    const canonical = productUrl(product.url);
    if (canonical.pathname.replace(/\/$/, '') !== productUrl(entry.url).pathname.replace(/\/$/, '')) {
      throw new Error('Wishlist product URL does not match.');
    }
    if (!Array.isArray(product.variants)) throw new Error('Wishlist variant data is unavailable.');
    const variant = product.variants.find((item) => identity(item?.id) === entry.variant);
    if (!variant) throw new Error('The saved variant is no longer available.');
    return { product, variant, url: canonical };
  }

  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (className) node.className = className;
    return node;
  }

  function loginUrl(returnUrl) {
    const url = new URL(config.login || '/account/login', location.origin);
    if (url.origin !== location.origin || url.username || url.password) throw new Error('Account login is unavailable.');
    url.searchParams.set('return_to', returnUrl.pathname + returnUrl.search + returnUrl.hash);
    return url.href;
  }

  async function refresh() {
    let entries = [];
    try {
      if (enabled && loggedIn) { await account.ready; entries = read(); }
    } catch (error) { message(`Wishlist is unavailable. ${error.message} Reload the page to retry when the service is available.`); return; }
    document.querySelectorAll('[data-ufm-wishlist]').forEach((button) => {
      const added = entries.some((entry) => entry.id === identity(button.dataset.productId));
      const title = button.dataset.productTitle || 'product';
      const pending = busy.has(identity(button.dataset.productId));
      button.setAttribute('aria-pressed', String(added));
      button.setAttribute('aria-label', added ? `Remove ${title} from Wishlist` : `Add ${title} to Wishlist`);
      if (pending) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      const label = button.querySelector('[data-ufm-wishlist-label]');
      if (label) label.textContent = added ? 'Saved to Wishlist' : 'Wishlist';
    });
  }

  async function render() {
    if (!page) return;
    const version = ++renderVersion;
    const container = page.querySelector('[data-ufm-wishlist-cards]');
    if (!loggedIn) {
      const link = element('a', 'Log in to view your wishlist');
      try { link.href = loginUrl(new URL(location.href)); }
      catch (error) { message(error.message); return; }
      container.replaceChildren(link);
      return;
    }
    if (!enabled) { container.replaceChildren(element('p', unavailable)); return; }
    container.replaceChildren(element('p', 'Loading your account wishlist...'));
    try {
      await account.ready;
      if (version !== renderVersion) return;
      const entries = read();
      const cards = await Promise.all(entries.map(async (entry) => {
        const card = element('article', null, 'ufm-wishlist-card');
        let title = `Product ${entry.id}`;
        try {
          const { product, variant, url } = await data(entry);
          title = typeof product.title === 'string' && product.title ? product.title : title;
          if (typeof variant.image === 'string' && variant.image) {
            const imageUrl = new URL(variant.image, location.origin);
            if (['http:', 'https:'].includes(imageUrl.protocol) && !imageUrl.username && !imageUrl.password) {
              const image = element('img');
              image.src = imageUrl.href;
              image.alt = title;
              image.width = 360;
              image.height = 270;
              image.loading = 'lazy';
              card.append(image);
            }
          }
          card.append(element('h2', title));
          card.append(element('p', typeof variant.price === 'string' && variant.price ? variant.price : 'Price unavailable', 'ufm-wishlist-price'));
          card.append(element('p', variant.available === true ? 'In stock' : variant.available === false ? 'Sold out' : 'Stock status unavailable'));
          const view = element('a', 'View product');
          url.searchParams.set('variant', entry.variant);
          view.href = url.href;
          card.append(view);
        } catch (error) {
          card.append(element('h2', title), element('p', `Product unavailable. ${error.message}`));
        }
        const remove = element('button', 'Remove product', 'ufm-wishlist-remove');
        remove.type = 'button';
        remove.setAttribute('data-ufm-wishlist', '');
        remove.setAttribute('data-ufm-wishlist-remove', '');
        remove.setAttribute('data-product-id', entry.id);
        remove.setAttribute('data-product-title', title);
        card.append(remove);
        return card;
      }));
      if (version !== renderVersion) return;
      container.replaceChildren(...(cards.length ? cards : [element('p', 'Your wishlist is empty. Save products using Wishlist.')]));
      await refresh();
    } catch (error) {
      if (version !== renderVersion) return;
      container.replaceChildren(element('p', 'Your account wishlist is unavailable. Reload the page to retry when the service is available.'));
      message(`Wishlist is unavailable. ${error.message} Reload the page to retry when the service is available.`);
    }
  }

  async function change(action, entry) {
    await account.ready;
    if (action === 'add') {
      const resolved = await data(entry);
      entry = { ...entry, url: resolved.url.pathname };
    }
    await account.mutate('wishlist', action, action === 'add' ? { entry } : { id: entry.id });
    const acknowledged = read().some((item) => item.id === entry.id && (action !== 'add' || item.variant === entry.variant));
    if (acknowledged !== (action === 'add')) throw new Error('The server did not confirm the wishlist change.');
    await refresh();
    await render();
    message(action === 'add' ? 'Product saved to Wishlist in your account.' : 'Product removed from Wishlist.');
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ufm-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const id = identity(button.dataset.productId);
    if (button.disabled || busy.has(id)) return;
    if (loggedIn && !enabled) { message(unavailable); return; }
    busy.add(id);
    button.setAttribute('aria-busy', 'true');
    try {
      if (!loggedIn) {
        const entry = normalize({ id, variant: currentVariant(button), url: button.dataset.productUrl });
        const returnUrl = new URL(location.href);
        returnUrl.searchParams.set('ufm_wishlist_product', entry.id);
        returnUrl.searchParams.set('ufm_wishlist_variant', entry.variant);
        returnUrl.searchParams.set('ufm_wishlist_url', entry.url);
        location.assign(loginUrl(returnUrl));
        return;
      }
      await account.ready;
      const remove = button.hasAttribute('data-ufm-wishlist-remove') || read().some((entry) => entry.id === id);
      if (!id) throw new Error('Invalid wishlist product ID.');
      const entry = remove ? { id } : normalize({ id, variant: currentVariant(button), url: button.dataset.productUrl });
      await change(remove ? 'remove' : 'add', entry);
    } catch (error) { message(`Not saved. ${error.message}`); }
    finally { busy.delete(id); button.removeAttribute('aria-busy'); await refresh(); }
  }, true);

  function currentVariant(button) {
    if (button.hasAttribute('data-ufm-wishlist-product-page')) {
      return button.closest('.shopify-section')?.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    }
    return button.dataset.variantId;
  }

  document.addEventListener('keydown', (event) => {
    const anchor = event.target.closest('a[data-ufm-wishlist]');
    if (anchor && event.key === ' ') { event.preventDefault(); anchor.click(); }
  });
  window.addEventListener('ufm:saved-lists-updated', () => { refresh(); render(); });
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 &&
      (node.matches('[data-ufm-wishlist]') || node.querySelector('[data-ufm-wishlist]'))))) refresh();
  }).observe(document.body, { childList: true, subtree: true });
  refresh();
  render();
  const pending = new URL(location.href);
  if (loggedIn && pending.searchParams.has('ufm_wishlist_product')) {
    if (!enabled) message(unavailable);
    else {
      (async () => {
        const entry = normalize({ id: pending.searchParams.get('ufm_wishlist_product'),
          variant: pending.searchParams.get('ufm_wishlist_variant'), url: pending.searchParams.get('ufm_wishlist_url') });
        if (!window.confirm('Save this product to your account wishlist?')) {
          message('Not saved. Please use Wishlist again to save this product.');
          return;
        }
        busy.add(entry.id);
        try {
          await change('add', entry);
          const clean = new URL(location.href);
          ['ufm_wishlist_product', 'ufm_wishlist_variant', 'ufm_wishlist_url'].forEach((name) => clean.searchParams.delete(name));
          history.replaceState(history.state, '', clean.pathname + clean.search + clean.hash);
        } finally { busy.delete(entry.id); await refresh(); }
      })().catch((error) => message(`Not saved. ${error.message} Reload the page to retry this pending save.`));
    }
  } else if (loggedIn && pending.searchParams.get('ufm_product_action') === 'wishlist') {
    message('Not saved. This wishlist login link is no longer supported. Please use Wishlist again to save this product.');
  }
})();
